import * as Yup from "yup";
import { shouldShowAddressState, validatePostalCode } from "services";
import { testOptions, englishTestRanges } from "types";

type TestType = "TOEFL" | "IELTS" | "PTE" | "CAE" | "CPE";

const isValidScore = function (
  this: any,
  score: number | undefined | null,
  testType: TestType | "",
): boolean {
  return (
    !testOptions ||
    score === undefined ||
    score === null ||
    (score >= englishTestRanges[testType as TestType].min &&
      score <= englishTestRanges[testType as TestType].max)
  );
};

export const applicationSchema = [
  Yup.object({
    GenuineStudentId: Yup.string(),
  }),

  Yup.object({
    firstName: Yup.string().required("Please enter a first name."),
    lastName: Yup.string().when("isSingleNameOnly", {
      is: false,
      then: (schema) => schema.required("Please enter a last name."),
      otherwise: (schema) => schema.notRequired(),
    }),
    dateOfBirth: Yup.date().required("Please enter your date of birth."),
    gender: Yup.string().required("Please select a gender."),
    customGender: Yup.string()
      .nullable()
      .when("gender", {
        is: (reasons: string[]) => reasons?.includes("other"),
        then: (schema) => schema.required("Please specify your gender"),
        otherwise: (schema) => schema.nullable(),
      }),
    countryOfBirth: Yup.string().required(
      "Please enter your country of birth.",
    ),
    countryOfCitizenship: Yup.string().required(
      "Please enter your country of citizenship.",
    ),
    maritalStatus: Yup.string().required("Please select your marital status."),
    registeredDateOfMarriage: Yup.date()
      .nullable()
      .when("maritalStatus", {
        is: "married",
        then: (schema) =>
          schema.required("Please enter your date of marriage."),
        otherwise: (schema) => schema.nullable(),
      }),

    // Phone validation - at least one phone number required
    phone: Yup.object({
      countryCode: Yup.string(),
      areaCode: Yup.string().nullable(),
      number: Yup.string(),
      extension: Yup.string().nullable(),
    }).test(
      "phone-required",
      "Please provide at least one phone number (phone or mobile).",
      function (value) {
        const { mobile } = this.parent;
        const hasPhone = value && value.number && value.number.trim();
        const hasMobile = mobile && mobile.number && mobile.number.trim();

        return hasPhone || hasMobile;
      },
    ),
    mobile: Yup.object({
      countryCode: Yup.string(),
      areaCode: Yup.string().nullable(),
      number: Yup.string(),
      extension: Yup.string().nullable(),
    }).test(
      "mobile-required",
      "Please provide at least one phone number (phone or mobile).",
      function (value) {
        const { phone } = this.parent;
        const hasPhone = phone && phone.number && phone.number.trim();
        const hasMobile = value && value.number && value.number.trim();

        return hasPhone || hasMobile;
      },
    ),

    // Current Address validation (flat structure)
    currentAddressStreet1: Yup.string().required("Street address is required."),
    currentAddressCity: Yup.string().required("City is required."),
    currentAddressState: Yup.string()
      .nullable()
      .test("state-required", "State/Province is required.", function (value) {
        const { currentAddressCountry } = this.parent;
        if (!currentAddressCountry) return true;

        try {
          const stateRequired = shouldShowAddressState(currentAddressCountry);
          return !stateRequired || !!(value && value.trim());
        } catch (error) {
          return !!(value && value.trim());
        }
      }),
    currentAddressPostalCode: Yup.string()
      .required("Postal code is required.")
      .test(
        "postal-code-format",
        "Invalid postal code format for this country.",
        function (value) {
          const { currentAddressCountry } = this.parent;
          if (!value || !currentAddressCountry) return true;

          try {
            const isValid = validatePostalCode(value, currentAddressCountry);
            return !!isValid;
          } catch (error) {
            return !!(value && value.trim());
          }
        },
      ),
    currentAddressCountry: Yup.string().required("Country is required."),

    homeAddressStreet1: Yup.string().required("Street address is required."),
    homeAddressCity: Yup.string().required("City is required."),
    homeAddressState: Yup.string()
      .nullable()
      .test("state-required", "State/Province is required.", function (value) {
        const { homeAddressCountry } = this.parent;
        if (!homeAddressCountry) return true;

        try {
          const stateRequired = shouldShowAddressState(homeAddressCountry);
          return !stateRequired || !!(value && value.trim());
        } catch (error) {
          return !!(value && value.trim());
        }
      }),
    homeAddressPostalCode: Yup.string()
      .required("Postal code is required.")
      .test(
        "postal-code-format",
        "Invalid postal code format for this country.",
        function (value) {
          const { homeAddressCountry } = this.parent;
          if (!value || !homeAddressCountry) return true;

          try {
            const isValid = validatePostalCode(value, homeAddressCountry);
            return !!isValid;
          } catch (error) {
            return !!(value && value.trim());
          }
        },
      ),
    homeAddressCountry: Yup.string().required("Country is required."),

    // Emergency Contact validation (flat structure)
    emergencyContactName: Yup.string().required(
      "Please enter an emergency contact name.",
    ),
    emergencyContactRelationship: Yup.string().required(
      "Please specify the relationship.",
    ),
    emergencyContactPhone: Yup.object({
      countryCode: Yup.string(),
      areaCode: Yup.string().nullable(),
      number: Yup.string(),
      extension: Yup.string().nullable(),
    }).test(
      "emergency-phone-required",
      "Please provide at least one emergency contact phone number (phone or mobile).",
      function (value) {
        const { phone } = this.parent;
        const hasPhone = phone && phone.number && phone.number.trim();
        const hasMobile = value && value.number && value.number.trim();

        return hasPhone || hasMobile;
      },
    ),
    emergencyContactMobile: Yup.object({
      countryCode: Yup.string(),
      areaCode: Yup.string().nullable(),
      number: Yup.string(),
      extension: Yup.string().nullable(),
    }).test(
      "emergency-mobile-required",
      "Please provide at least one emergency contact phone number (phone or mobile).",
      function (value) {
        const { phone } = this.parent;
        const hasPhone = phone && phone.number && phone.number.trim();
        const hasMobile = value && value.number && value.number.trim();

        return hasPhone || hasMobile;
      },
    ),

    // Passport validation
    passportNumber: Yup.string().required("Please enter your passport number."),
    passportExpiryDate: Yup.date()
      .nullable()
      .required("Please enter passport expiry date."),
    passportIssuingCountry: Yup.string().required(
      "Please enter passport issuing country.",
    ),

    // Visa Details validation
    isCurrentlyInCountry: Yup.boolean()
      .nullable()
      .required("Please select whether you are currently in the country."),
    yearOfFirstArrival: Yup.date()
      .nullable()
      .when("isCurrentlyInCountry", {
        is: true,
        then: (schema) =>
          schema.required("Please enter year of first arrival."),
        otherwise: (schema) => schema.notRequired(),
      }),
    hasCurrentVisa: Yup.boolean()
      .nullable()
      .required("Please select whether you currently hold a visa."),
    isApplyStudentVisa: Yup.boolean()
      .nullable()
      .when("hasCurrentVisa", {
        is: "false",
        then: (schema) =>
          schema.required(
            "Please select whether you will apply for a student visa.",
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    applicationType: Yup.string()
      .nullable()
      .when("isApplyStudentVisa", {
        is: "offshore",
        then: (schema) => schema.required("Please select application type."),
        otherwise: (schema) => schema.notRequired(),
      }),
    applicationCountry: Yup.string()
      .nullable()
      .when("applicationType", {
        is: "offshore",
        then: (schema) => schema.required("Please enter the country."),
        otherwise: (schema) => schema.notRequired(),
      }),
    hasEntryRefusal: Yup.boolean()
      .nullable()
      .required("Please select whether you have been refused entry."),
    entryRefusalDetails: Yup.string().when("hasEntryRefusal", {
      is: true,
      then: (schema) => schema.required("Please provide details."),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),

  // English Proficiency Schema
  Yup.object({
    isEnglishFirstLanguage: Yup.boolean(),
    firstLanguage: Yup.string().when("isEnglishFirstLanguage", {
      is: false,
      then: (schema) => schema.required("Please enter your first language"),
      otherwise: (schema) => schema.nullable(),
    }),
    hasEnglishInstructionStudies: Yup.boolean().nullable(),
    englishInstructionStudiesCompleted: Yup.string()
      .nullable()
      .when("hasEnglishInstructionStudies", {
        is: true,
        then: (schema) =>
          schema.required("Please describe your English instruction studies"),
        otherwise: (schema) => schema.nullable(),
      }),
    englishInstructionStudiesDuration: Yup.string()
      .nullable()
      .when("hasEnglishInstructionStudies", {
        is: true,
        then: (schema) =>
          schema.required("Please enter the duration of your studies"),
        otherwise: (schema) => schema.nullable(),
      }),

    englishTest: Yup.string().required("Please select a test type"),
    reading: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter a reading score"),
        otherwise: (schema) => schema.nullable(),
      }),
    writing: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter a writing score"),
        otherwise: (schema) => schema.nullable(),
      }),
    speaking: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter a speaking score"),
        otherwise: (schema) => schema.nullable(),
      }),
    listening: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter a listening score"),
        otherwise: (schema) => schema.nullable(),
      }),
    useOfEnglish: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val === "CAE" || val === "CPE",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter a use of English score"),
        otherwise: (schema) => schema.nullable(),
      }),
    overallScore: Yup.number()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType,
                );
              },
            )
            .required("Please enter an overall score"),
        otherwise: (schema) => schema.nullable(),
      }),
    dateOfCompletion: Yup.date()
      .nullable()
      .when("englishTest", {
        is: (val: string) => val && val !== "",
        then: (schema) => schema.required("Please choose a test date"),
        otherwise: (schema) => schema.nullable(),
      }),
    testReportFormNumber: Yup.string().nullable(),

    hasELICOS: Yup.boolean(),
    elicosInstitution: Yup.string()
      .nullable()
      .when("hasELICOS", {
        is: true,
        then: (schema) =>
          schema.required("Please enter the ELICOS institution"),
        otherwise: (schema) => schema.nullable(),
      }),
    elicosStartDate: Yup.date()
      .nullable()
      .when("hasELICOS", {
        is: true,
        then: (schema) => schema.required("Please enter the ELICOS start date"),
        otherwise: (schema) => schema.nullable(),
      }),
    elicosEndDate: Yup.date()
      .nullable()
      .when("hasELICOS", {
        is: true,
        then: (schema) => schema.required("Please enter the ELICOS end date"),
        otherwise: (schema) => schema.nullable(),
      }),
    elicosWeeks: Yup.number()
      .nullable()
      .when("hasELICOS", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Must be at least 1 week")
            .required("Please enter the duration in weeks"),
        otherwise: (schema) => schema.nullable(),
      }),
  }),

  // Academic History Schema
  Yup.object({
    // Education Overview
    completedYear12InAustralia: Yup.boolean().required(
      "Please select yes or no",
    ),
    atarScore: Yup.number().when("completedYear12InAustralia", {
      is: true,
      then: (schema) => schema.required("Please provide an ATAR score"),
      otherwise: (schema) => schema.nullable(),
    }),
    hasBeenExcluded: Yup.boolean().required("Please select yes or no"),
    exclusionDetails: Yup.string().when("hasBeenExcluded", {
      is: true,
      then: (schema) => schema.required("Please provide exclusion details"),
      otherwise: (schema) => schema.nullable(),
    }),
    usiNumber: Yup.string().nullable(),

    // Credit Transfer validation
    isApplyingForCreditTransfer: Yup.boolean().required(
      "Please select yes or no",
    ),
    creditTransfer: Yup.array().when("isApplyingForCreditTransfer", {
      is: true,
      then: (schema) =>
        schema.of(
          Yup.object({
            institutionName: Yup.string().test(
              "conditional-required",
              "Please enter institution name",
              function (value) {
                const pathMatch = this.path.match(/\[(\d+)\]/);
                const index = pathMatch ? parseInt(pathMatch[1]) : 0;
                if (index === 0) return !!value;
                const { courseCode, courseName, dateCompleted } = this.parent;
                const hasAnyValue = [
                  courseCode,
                  courseName,
                  dateCompleted,
                ].some((val) => val && val !== "");
                return hasAnyValue ? !!value : true;
              },
            ),
            courseCode: Yup.string().test(
              "conditional-required",
              "Please enter course code",
              function (value) {
                const pathMatch = this.path.match(/\[(\d+)\]/);
                const index = pathMatch ? parseInt(pathMatch[1]) : 0;
                if (index === 0) return !!value;
                const { institutionName, courseName, dateCompleted } =
                  this.parent;
                const hasAnyValue = [
                  institutionName,
                  courseName,
                  dateCompleted,
                ].some((val) => val && val !== "");
                return hasAnyValue ? !!value : true;
              },
            ),
            courseName: Yup.string().test(
              "conditional-required",
              "Please enter course name",
              function (value) {
                const pathMatch = this.path.match(/\[(\d+)\]/);
                const index = pathMatch ? parseInt(pathMatch[1]) : 0;
                if (index === 0) return !!value;
                const { institutionName, courseCode, dateCompleted } =
                  this.parent;
                const hasAnyValue = [
                  institutionName,
                  courseCode,
                  dateCompleted,
                ].some((val) => val && val !== "");
                return hasAnyValue ? !!value : true;
              },
            ),
            dateCompleted: Yup.date()
              .nullable()
              .test(
                "conditional-required",
                "Please select completion date",
                function (value) {
                  const pathMatch = this.path.match(/\[(\d+)\]/);
                  const index = pathMatch ? parseInt(pathMatch[1]) : 0;
                  if (index === 0) return !!value;
                  const { institutionName, courseCode, courseName } =
                    this.parent;
                  const hasAnyValue = [
                    institutionName,
                    courseCode,
                    courseName,
                  ].some((val) => val && val !== "");
                  return hasAnyValue ? !!value : true;
                },
              ),
            equivalentCode: Yup.string().nullable(),
            equivalentName: Yup.string().nullable(),
          }),
        ),
      otherwise: (schema) => schema.nullable(),
    }),

    // High School (flat structure)
    highSchoolName: Yup.string().required("Please enter your high school name"),
    highSchoolCity: Yup.string().required("Please enter city"),
    highSchoolCountry: Yup.string().required("Please enter country"),
    highSchoolGradeObtained: Yup.string().required("Please enter a grade"),
    highSchoolStartDate: Yup.date().required("Please provide a start date"),
    highSchoolEndDate: Yup.date().when("highSchoolIsStillAttending", {
      is: false,
      then: (schema) => schema.required("Please provide an end date"),
      otherwise: (schema) => schema.nullable(),
    }),

    // Tertiary 1 (flat structure)
    tertiary1School: Yup.string().required("Please enter institution name"),
    tertiary1Course: Yup.string().required("Please enter course name"),
    tertiary1City: Yup.string().required("Please enter city"),
    tertiary1Country: Yup.string().required("Please enter country"),
    tertiary1GradeObtained: Yup.string().required("Please enter a grade"),
    tertiary1StartDate: Yup.date().required("Please provide a start date"),
    tertiary1EndDate: Yup.date().when("tertiary1IsStillAttending", {
      is: false,
      then: (schema) => schema.required("Please provide an end date"),
      otherwise: (schema) => schema.nullable(),
    }),

    // Tertiary 2 (conditional validation - flat structure)
    tertiary2School: Yup.string().test(
      "conditional-required",
      "Please enter institution name",
      function (value) {
        const {
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent || {};
        const hasAnyValue = [
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary2Course: Yup.string().test(
      "conditional-required",
      "Please enter course name",
      function (value) {
        const {
          tertiary2School,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary2City: Yup.string().test(
      "conditional-required",
      "Please enter city",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary2Country: Yup.string().test(
      "conditional-required",
      "Please enter country",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary2GradeObtained: Yup.string().test(
      "conditional-required",
      "Please enter a grade",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary2StartDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide a start date",
        function (value) {
          const {
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2EndDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2EndDate,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),
    tertiary2EndDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide an end date",
        function (value) {
          const {
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2StartDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2StartDate,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),

    // Tertiary 3 (conditional validation - flat structure)
    tertiary3School: Yup.string().test(
      "conditional-required",
      "Please enter institution name",
      function (value) {
        const {
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary3Course: Yup.string().test(
      "conditional-required",
      "Please enter course name",
      function (value) {
        const {
          tertiary3School,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary3City: Yup.string().test(
      "conditional-required",
      "Please enter city",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary3Country: Yup.string().test(
      "conditional-required",
      "Please enter country",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary3GradeObtained: Yup.string().test(
      "conditional-required",
      "Please enter a grade",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    tertiary3StartDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide a start date",
        function (value) {
          const {
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3EndDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3EndDate,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),
    tertiary3EndDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide an end date",
        function (value) {
          const {
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3StartDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3StartDate,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),

    currentlyStudyingAt: Yup.string().nullable(),
  }),

  // Student Support Schema
  Yup.object({
    hasScholarshipSponsorship: Yup.boolean().required(
      "Please select yes or no",
    ),
    scholarshipProviderName: Yup.string()
      .nullable()
      .when("hasScholarshipSponsorship", {
        is: true,
        then: (schema) =>
          schema.required("Please provide scholarship provider name"),
        otherwise: (schema) => schema.nullable(),
      }),
    allowSponsorProgressSharing: Yup.boolean()
      .nullable()
      .when("hasScholarshipSponsorship", {
        is: true,
        then: (schema) => schema.required("Please select yes or no"),
        otherwise: (schema) => schema.nullable(),
      }),
    payingMoreThan50Percent: Yup.boolean().required("Please select yes or no"),
    hasCurrentOSHC: Yup.boolean().required("Please select yes or no"),
    oshcProviderName: Yup.string()
      .nullable()
      .when("hasCurrentOSHC", {
        is: true,
        then: (schema) => schema.required("Please provide provider name"),
        otherwise: (schema) => schema.nullable(),
      }),
    oshcMembershipNumber: Yup.string()
      .nullable()
      .when("hasCurrentOSHC", {
        is: true,
        then: (schema) => schema.required("Please provide membership number"),
        otherwise: (schema) => schema.nullable(),
      }),
    oshcExpiryDate: Yup.date()
      .nullable()
      .when("hasCurrentOSHC", {
        is: true,
        then: (schema) => schema.required("Please provide expiry date"),
        otherwise: (schema) => schema.nullable(),
      }),
    wantUniversityToArrangeOSHC: Yup.boolean()
      .nullable()
      .when("hasCurrentOSHC", {
        is: false,
        then: (schema) => schema.required("Please select yes or no"),
        otherwise: (schema) => schema.nullable(),
      }),
    oshcCoverageType: Yup.string()
      .nullable()
      .when("wantUniversityToArrangeOSHC", {
        is: true,
        then: (schema) => schema.required("Please select coverage type"),
        otherwise: (schema) => schema.nullable(),
      }),
    hasDisabilityMedicalCondition: Yup.boolean().required(
      "Please select yes or no",
    ),
    disabilityType: Yup.array().when("hasDisabilityMedicalCondition", {
      is: true,
      then: (schema) =>
        schema.min(1, "Please select at least one disability type"),
      otherwise: (schema) => schema.nullable(),
    }),
    otherDetails: Yup.string()
      .nullable()
      .when("disabilityType", {
        is: (types: string[]) => types?.includes("other"),
        then: (schema) => schema.required("Please specify other condition"),
        otherwise: (schema) => schema.nullable(),
      }),
    disabilityStartDate: Yup.date()
      .nullable()
      .when("hasDisabilityMedicalCondition", {
        is: true,
        then: (schema) => schema.required("Please provide start date"),
        otherwise: (schema) => schema.nullable(),
      }),
  }),

  // Employment History Schema
  Yup.object({
    currentEmploymentStatus: Yup.string().required(
      "Please select your current employment status",
    ),
    reasonForUndertakingCourse: Yup.array().min(
      1,
      "Please select at least one reason",
    ),
    reasonOtherDetails: Yup.string()
      .nullable()
      .when("reasonForUndertakingCourse", {
        is: (reasons: string[]) => reasons?.includes("other"),
        then: (schema) => schema.required("Please specify other reason"),
        otherwise: (schema) => schema.nullable(),
      }),

    employment1CompanyName: Yup.string().test(
      "conditional-required",
      "Please enter company name",
      function (value) {
        const {
          employment1PositionAndDuties,
          employment1DateCommenced,
          employment1DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment1PositionAndDuties,
          employment1DateCommenced,
          employment1DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment1PositionAndDuties: Yup.string().test(
      "conditional-required",
      "Please enter position and duties",
      function (value) {
        const {
          employment1CompanyName,
          employment1DateCommenced,
          employment1DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment1CompanyName,
          employment1DateCommenced,
          employment1DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment1DateCommenced: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide start date",
        function (value) {
          const {
            employment1CompanyName,
            employment1PositionAndDuties,
            employment1DateEnded,
          } = this.parent;
          const hasAnyValue = [
            employment1CompanyName,
            employment1PositionAndDuties,
            employment1DateEnded,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),
    employment1DateEnded: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide end date",
        function (value) {
          const {
            employment1CompanyName,
            employment1PositionAndDuties,
            employment1DateCommenced,
            employment1IsStillWorking,
          } = this.parent;
          const hasAnyValue = [
            employment1CompanyName,
            employment1PositionAndDuties,
            employment1DateCommenced,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue && !employment1IsStillWorking ? !!value : true;
        },
      ),

    employment2CompanyName: Yup.string().test(
      "conditional-required",
      "Please enter company name",
      function (value) {
        const {
          employment2PositionAndDuties,
          employment2DateCommenced,
          employment2DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment2PositionAndDuties,
          employment2DateCommenced,
          employment2DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment2PositionAndDuties: Yup.string().test(
      "conditional-required",
      "Please enter position and duties",
      function (value) {
        const {
          employment2CompanyName,
          employment2DateCommenced,
          employment2DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment2CompanyName,
          employment2DateCommenced,
          employment2DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment2DateCommenced: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide start date",
        function (value) {
          const {
            employment2CompanyName,
            employment2PositionAndDuties,
            employment2DateEnded,
          } = this.parent;
          const hasAnyValue = [
            employment2CompanyName,
            employment2PositionAndDuties,
            employment2DateEnded,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue ? !!value : true;
        },
      ),
    employment2DateEnded: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide end date",
        function (value) {
          const {
            employment2CompanyName,
            employment2PositionAndDuties,
            employment2DateCommenced,
            employment2IsStillWorking,
          } = this.parent;
          const hasAnyValue = [
            employment2CompanyName,
            employment2PositionAndDuties,
            employment2DateCommenced,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue && !employment2IsStillWorking ? !!value : true;
        },
      ),

    employment3CompanyName: Yup.string().test(
      "conditional-required",
      "Please enter company name",
      function (value) {
        const {
          employment3PositionAndDuties,
          employment3DateCommenced,
          employment3DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment3PositionAndDuties,
          employment3DateCommenced,
          employment3DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment3PositionAndDuties: Yup.string().test(
      "conditional-required",
      "Please enter position and duties",
      function (value) {
        const {
          employment3CompanyName,
          employment3DateCommenced,
          employment3DateEnded,
        } = this.parent;
        const hasAnyValue = [
          employment3CompanyName,
          employment3DateCommenced,
          employment3DateEnded,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      },
    ),
    employment3DateCommenced: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide start date",
        function (value) {
          const {
            employment3CompanyName,
            employment3PositionAndDuties,
            employment3DateEnded,
          } = this.parent;
          const hasAnyValue = [
            employment3CompanyName,
            employment3PositionAndDuties,
            employment3DateEnded,
          ].some((val) => val && val !== "");
          return hasAnyValue ? !!value : true;
        },
      ),
    employment3DateEnded: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide end date",
        function (value) {
          const {
            employment3CompanyName,
            employment3PositionAndDuties,
            employment3DateCommenced,
            employment3IsStillWorking,
          } = this.parent;
          const hasAnyValue = [
            employment3CompanyName,
            employment3PositionAndDuties,
            employment3DateCommenced,
          ].some((val) => {
            if (val instanceof Date) return true;
            return val && val !== "";
          });
          return hasAnyValue && !employment3IsStillWorking ? !!value : true;
        },
      ),
  }),

  // Checklist Schema
  Yup.object({
    checklist: Yup.object({
      isComplete: Yup.boolean().test(
        "checklist-complete",
        "Please complete all required checklist items before proceeding",
        (value) => value === true,
      ),
    }).required("Checklist is required"),
  }),

  // Declaration Schema
  Yup.object({
    declarations: Yup.object({
      mandatory: Yup.array()
        .of(
          Yup.object({
            id: Yup.string().required(),
            text: Yup.string().required(),
            isAccepted: Yup.boolean().required(),
          }),
        )
        .min(17, "Please accept all mandatory declarations")
        .test(
          "all-required-declarations",
          "Please accept all mandatory declarations",
          function (value) {
            const requiredDeclarations = [
              "informationAccuracy",
              "courseUnderstanding",
              "informationAccessAuthorization",
              "costAwareness",
              "financialCapability",
              "privacyAndInformationSharing",
              "ageDeclaration",
              "consequencesUnderstanding",
              "agentAndThirdPartyProcessing",
              "verificationAuthorization",
              "changeNotification",
              "visaAndLegalCompliance",
              "policyAcceptance",
              "contactDetailsResponsibility",
              "documentationAndRecordRetention",
              "thirdPartyDisclosureLimitations",
              "finalConfirmationAndDigitalSignature",
            ];
            return requiredDeclarations.every((req) => {
              const declaration = value?.find((d: any) => d.id === req);
              return declaration && declaration.isAccepted === true;
            });
          },
        ),
      marketing: Yup.array().of(
        Yup.object({
          id: Yup.string().required(),
          text: Yup.string().required(),
          isAccepted: Yup.boolean().required(),
        }),
      ),
    }).required("Declaration section is required"),
  }),
];
